import "./DataSettingsPane.css";
import "./settings-ui.css";
import { useEffect, useState } from "preact/hooks";
import { DestructiveActionButton } from "../../components/DestructiveActionButton/DestructiveActionButton.jsx";
import { DashboardPage, createDashboardLayout } from "../../components/DashboardPage/DashboardPage.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { StatusMessage } from "../../components/StatusMessage/StatusMessage.jsx";
import { localizeErrorMessage } from "../../lib/localizeErrorMessage.js";
import {
  archiveProfileYear,
  createProfileYear,
  deleteProfileYear,
  listProfileYears,
  unarchiveProfileYear
} from "../../lib/storage/walletWatchDb.js";

const dataPaneLayout = createDashboardLayout({
  columns: [
    { size: "fill" },
    { size: "fill" }
  ],
  rows: [
    { size: "fill" }
  ],
  areas: [
    ["years", "create"]
  ]
});

function getYearMeta(yearRecord, activeYear) {
  if (yearRecord.year === activeYear) {
    return "current";
  }

  if (yearRecord.archivedAt) {
    return "archived";
  }

  return "available";
}

function footerToneClass(type) {
  if (type === "success") {
    return "tone-success";
  }

  if (type === "warning") {
    return "tone-warning";
  }

  if (type === "error") {
    return "tone-error";
  }

  return "";
}

export function DataSettingsPane({ activeScope, onScopeChanged, t = (key) => key }) {
  const [years, setYears] = useState([]);
  const [status, setStatus] = useState(null);
  const [newYear, setNewYear] = useState("");

  async function loadYears(profileId) {
    if (!profileId) {
      setYears([]);
      return;
    }

    const nextYears = await listProfileYears(profileId, { includeArchived: true });
    setYears(nextYears);
  }

  useEffect(() => {
    loadYears(activeScope?.profileId).catch((error) => {
      console.error("Failed to load profile years.", error);
      setStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.dataSettings.failedToLoadYears")
      });
    });
  }, [activeScope?.profileId, activeScope?.year, t]);

  async function handleArchiveYear(year) {
    if (!activeScope?.profileId) {
      return;
    }

    try {
      const result = await archiveProfileYear(activeScope.profileId, year);

      await onScopeChanged?.(result.nextScope ?? activeScope);

      await loadYears(result.nextScope?.profileId ?? activeScope.profileId);
      setStatus({
        type: "success",
        message: result.nextScope
          ? `${t("messages.dataSettings.archived")} ${year}. ${t("messages.dataSettings.switchedTo")} ${result.nextScope.year}.`
          : `${t("messages.dataSettings.archived")} ${year}.`
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.dataSettings.failedToArchiveYear")
      });
    }
  }

  async function handleUnarchiveYear(year) {
    if (!activeScope?.profileId) {
      return;
    }

    try {
      await unarchiveProfileYear(activeScope.profileId, year);
      await onScopeChanged?.(activeScope);
      await loadYears(activeScope.profileId);
      setStatus({
        type: "success",
        message: `${t("messages.dataSettings.unarchived")} ${year}.`
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.dataSettings.failedToUnarchiveYear")
      });
    }
  }

  async function handleDeleteYear(year) {
    if (!activeScope?.profileId) {
      return;
    }

    try {
      const result = await deleteProfileYear(activeScope.profileId, year);

      await onScopeChanged?.(result.nextScope ?? activeScope);

      await loadYears(result.nextScope?.profileId ?? activeScope.profileId);
      setStatus({
        type: "success",
        message: result.nextScope
          ? `${t("messages.dataSettings.deleted")} ${year}. ${t("messages.dataSettings.switchedTo")} ${result.nextScope.year}.`
          : `${t("messages.dataSettings.deleted")} ${year}.`
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.dataSettings.failedToDeleteYear")
      });
    }
  }

  async function handleCreateYear() {
    if (!activeScope?.profileId) {
      return;
    }

    const trimmedYear = String(newYear ?? "").trim();
    if (!/^\d{4}$/.test(trimmedYear)) {
      setStatus({
        type: "error",
        message: t("messages.dataSettings.newYearMustBeFourDigits")
      });
      return;
    }

    try {
      const result = await createProfileYear(activeScope.profileId, trimmedYear);
      await onScopeChanged?.(result.scope);
      await loadYears(activeScope.profileId);
      setNewYear("");
      setStatus({
        type: "success",
        message: `${t("messages.dataSettings.created")} ${trimmedYear}. ${t("messages.dataSettings.switchedTo")} ${trimmedYear}.`
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.dataSettings.failedToCreateYear")
      });
    }
  }

  return (
    <DashboardPage
      layout={dataPaneLayout}
      slots={[
        {
          key: "years",
          area: "years",
          content: (
            <Panel
              padded
              scroll
              className="settings-data-panel"
              header={t("years")}
              footerClassName={footerToneClass(status?.type)}
              footer={status ? (
                <StatusMessage type={status.type} message={status.message} />
              ) : (
                t("messages.dataSettings.archivedYearsStayInDatabase")
              )}
            >
              <div class="settings-form-stack">
                <div class="settings-item-list">
                  {years.map((yearRecord) => (
                    <div
                      key={yearRecord.id}
                      class={`settings-record${yearRecord.year === activeScope?.year ? " active" : ""}`}
                    >
                      <div class="settings-record-body">
                        <span class="settings-item-label">{yearRecord.year}</span>
                        <span class="settings-item-meta">{t(getYearMeta(yearRecord, activeScope?.year))}</span>
                      </div>

                      <div class="settings-record-actions">
                        {yearRecord.archivedAt ? (
                          <>
                            <button
                              class="settings-action-button"
                              type="button"
                              onClick={() => handleUnarchiveYear(yearRecord.year)}
                            >
                              {t("unarchive")}
                            </button>
                            <DestructiveActionButton
                              onConfirm={() => handleDeleteYear(yearRecord.year)}
                            >
                              {t("delete")}
                            </DestructiveActionButton>
                          </>
                        ) : (
                          <button
                            class="settings-action-button"
                            type="button"
                            onClick={() => handleArchiveYear(yearRecord.year)}
                          >
                            {t("archive")}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          )
        },
        {
          key: "create",
          area: "create",
          content: (
            <Panel
              padded
              header={t("messages.dataSettings.createYear")}
              footer={t("messages.dataSettings.createYearFooter")}
            >
              <div class="settings-form-stack">
                <div class="settings-field-row">
                  <label class="settings-field">
                    <span class="settings-field-label">{t("year")}</span>
                    <input
                      class="settings-text-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{4}"
                      maxLength={4}
                      value={newYear}
                      placeholder={String(Number(activeScope?.year ?? new Date().getFullYear()) + 1)}
                      onInput={(event) => {
                        setNewYear(event.currentTarget.value.replace(/[^0-9]/g, "").slice(0, 4));
                      }}
                    />
                  </label>

                  <div class="settings-actions">
                    <button
                      class="settings-action-button primary"
                      type="button"
                      onClick={handleCreateYear}
                    >
                      {t("messages.dataSettings.createYear")}
                    </button>
                  </div>
                </div>
              </div>
            </Panel>
          )
        }
      ]}
    />
  );
}
